import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Message, Progress } from 'semantic-ui-react'
import { FormProvider, useForm } from 'react-hook-form';
import Layout from '~/components/Layout';
import { handleErrors } from '~/utils/helpers';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import notify from '~/components/Notify';
import { RouteTypes } from '~/routes/constants';
import { Nullable } from '~/types';
import { checkPercentage, checkPercentageChild, getNumberOfFeeds, getStepHeadMessage, getTransformFeedGroups, prepareDataForPayload } from './utils';
import cloneDeep from 'lodash.clonedeep'
import Dialog from '~/components/Dialog';
import { ClimaCheckFeedType } from '~/api/services/climaCheck';
import CO2Service, { CO2OptionsType, Co2ResultsType } from '~/api/services/co2';
import { allSteps, RENEWABLE_ENERGY_PROD_SLUG, StepId } from './constants'

// STYLES
import { CSSProperties, ThemeContext } from 'styled-components';
import { CheckClimaEl } from '../style'
import { AnsweredContainer, LogoWrapper, SubmitHeader } from './style';

// STEPS
import StepsContainer from './StepsContainer';
import FourthStep from './Steps/FourthStep';
import ThirdStep from './Steps/ThirdStep';
import EnergyModal from './Steps/EnergyModal';
import FirstStep from './Steps/FirstStep';
import SecondStep from './Steps/SecondStep';
import SixthStep from './Steps/SixthStep';
import FifthStep from './Steps/FifthStep';
import SeventhStep from './Steps/SeventhStep';
import EightStep from './Steps/EightStep';
import NineStep from './Steps/NineStep';
import TenStep from './Steps/TenStep';
import ElevenStep from './Steps/ElevenStep';
import TwelveStep from './Steps/TwelveStep';
import ThirteenStep from './Steps/ThirteenStep';
import FourteenStep from './Steps/FourteenStep';
import FifthteenStep from './Steps/FifthteenStep';
import GrassSilageStep from './Steps/GrassSilageStep';
import CornSilageStep from './Steps/CornSilageStep';
import ForageFeed from './Steps/ForageFeed';
import ResultsCo2 from './Steps/ResultsCo2';
import { getCowTotalDryMatter, getTransformCowsFeedGroups, validateYoungCattleFeedTotals } from '../../ClimateCheck/AnswerClimateCheck/utils';
import { WarningDryMatterType, WarningHeiferType } from '../../ClimateCheck/AnswerClimateCheck';
import DryMatterValuesModal from '../../ClimateCheck/AnswerClimateCheck/Steps/TwelveStep/DryMatterValuesModal';
import YoungCattleFeedValidationModal from '../../ClimateCheck/AnswerClimateCheck/Steps/ThirteenStep/YoungCattleFeedValidationModal';

export const FeedPageContext = createContext(null)
export const StepsContext = createContext(null)

export type StepType = {
  id: string
  title: string
  icon?: string
  image?: string
  style?: CSSProperties
  calfStep?: boolean
  forageStep?: boolean
  feedGroupTab?: boolean
  futterbauStep?: boolean
}

const firstStepFields = ['totalMilkAmount', 'fat', 'protein', 'milkPrice', 'ureaContent']
const MIN_DLG_CALVING_AGE = 24
const MAX_DLG_CALVING_AGE = 30
export const GRASS_SILAGE_ID = 1
export const CORN_SILAGE_ID = 2
const FIRST_STEP_INDEX = 0
const MAX_FUTTERBAU_STEP = 2

const AnswerCO2Climate: React.FC = () => {
  const { t } = useTranslation()
  const { id, farmerId } = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  const { logoTitle } = useContext(ThemeContext)

  const [isBusy, setIsBusy] = useState(false)
  const [activeTab, setActiveTab] = useState(0)
  const [feedPage, setFeedPage] = useState(0)
  const [options, setOptions] = useState<Nullable<CO2OptionsType>>(null)
  const [isLoadingResults, setIsLoadingResults] = useState(false)
  const [energyCalcData, setEnergyCalcData] = useState(null)
  const [results, setResults] = useState<Co2ResultsType>(null)
  const [loadingEnergyData, setLoadingEnergyData] = useState(false)
  const [isEnergyDataFetching, setIsEnergyDataFetching] = useState(false)
  const [isEnergyModalOpen, setIsEnergyModalOpen] = useState(false)
  const [isSubmitModalOpen, setIsSubmitModalOpen] = useState(false)
  const [steps, setSteps] = useState<StepType[]>(allSteps)
  const [lastStep, setLastStep] = useState(0)

  const [dryMatterValidationModal, setDryMatterValidationModal] = useState<{
    isOpen: boolean
    warnings: WarningDryMatterType[]
    errors: WarningDryMatterType[]
  }>({ isOpen: false, warnings: [], errors: [] })
  const [youngCattleFeedValidationModal, setYoungCattleFeedValidationModal] = useState<{
    isOpen: boolean
    warnings: WarningHeiferType[]
    errors: WarningHeiferType[]
  }>({ isOpen: false, warnings: [], errors: [] })

  const methods = useForm({
    'mode': 'onSubmit'
  })

  const { watch, setValue } = methods

  const watchBreedField = watch('breeds')
  const watchFeedGroups = watch('feedGroups')
  const watchPhaseFeeding = watch('phaseFeeding')
  const watchRearingPhaseType = watch('rearingPhasesType')
  const watchRaisesHeifers = watch('raiseHeifersOnFarm')
  const watchGrowSilage = watch('silageTypes')

  const activeTabRef = useRef(activeTab)
  const isBusyRef = useRef(isBusy)

  useEffect(() => {
    fetchOptions()
  }, [t, location.pathname])

  useEffect(() => {
    if (activeTab > lastStep) {
      setLastStep(activeTab)
    }
  }, [activeTab, lastStep])

  useEffect(() => {
    const isFeedRationsTabsActive = getFeedRationsTabActive()
    if (!isFeedRationsTabsActive && energyCalcData) {
      setEnergyCalcData(null)
    }
  }, [activeTab, energyCalcData])

  useEffect(() => {
    const isFeedRationsTabsActive = getFeedRationsTabActive()

    if (isFeedRationsTabsActive) {
      setEnergyCalcData(null)
    }
  }, [watchFeedGroups])

  useEffect(() => {
    if (options) {
      updateSteps();
    }
  }, [options, watchRaisesHeifers, watchGrowSilage])

  useEffect(() => {
    activeTabRef.current = activeTab
  }, [activeTab])
  
  useEffect(() => {
    isBusyRef.current = isBusy
  }, [isBusy])
  
  useEffect(() => {
    const keyFields = new Set(['cowsNumber', 'calvingPeriod', 'dryPeriod', 'firstCalvingAge', 'youngCattleWeightBeforeCalving', 'youngCattleNumber'])
    const subscription = methods.watch((_, { name }) => {
      if (isBusyRef.current || !keyFields.has(name)) return
      setLastStep(activeTabRef.current)
    })
    return () => subscription.unsubscribe()
  }, [])

  function updateSteps() {
    let filteredSteps = [...allSteps];
    const hasGrassSilageStep = watchGrowSilage?.find(silage => silage === GRASS_SILAGE_ID);
    const hasMaiseSillageStep = watchGrowSilage?.find(silage => silage === CORN_SILAGE_ID)
    const hasHomeGrownStep = Boolean(hasGrassSilageStep) || Boolean(hasMaiseSillageStep)
    if (!watchRaisesHeifers) {
      filteredSteps = filteredSteps.filter(step => !step.calfStep);
    }

    if (!hasGrassSilageStep) {
      filteredSteps = filteredSteps.filter(step => step.id !== StepId.GRASS_SILAGE);
      // methods.setValue('grassSilage', null)
    }

    if (!hasMaiseSillageStep) {
      filteredSteps = filteredSteps.filter(step => step.id !== StepId.CORN_SILAGE);
      // methods.setValue('maizeSilage', null)
    }

    if (!hasHomeGrownStep) {
      filteredSteps = filteredSteps.filter(step => step.id !== StepId.FORAGE_FEEDS);
    }

    if (!options.useAdditivesTabOnCollectingData) {
      filteredSteps = filteredSteps.filter(step => step.id !== StepId.ADDITIVES_USAGE);
    }

    if (filteredSteps.length !== steps.length) {
      // Steps array restructured — old lastStep index is now invalid
      setLastStep(activeTab)
    }

    setSteps(filteredSteps);
  }

  function getFeedRationsTabActive() {
    const cowsFeedRationsTab = steps.findIndex(step => step.id === StepId.DAIRY_COW_FEED)
    const heifersFeedRationsTab = steps.findIndex(step => step.id === StepId.HEIFER_FEED)
    const isFeedCowFeedTabActive = activeTab === cowsFeedRationsTab
    const isHeiferFeedTabActive = activeTab === heifersFeedRationsTab

    return watchRaisesHeifers ?
      isFeedCowFeedTabActive || isHeiferFeedTabActive :
      isFeedCowFeedTabActive
  }

  async function handleCo2Initial() {
    try {
      const response = await CO2Service.getCO2AnswerOptions(id, { farmerId })
      const responsePreviousData = await CO2Service.getCO2PreviousData(id)
      let feedOptions = {}
      let younCalvesFeedOptions = {}
      if(responsePreviousData.data?.breeds?.length > 0) {
        const predefinedResponse = await CO2Service.getCowPredData(id, { breeds: responsePreviousData.data.breeds, tax: 22, farmerId })
        feedOptions = {
          juicyFeeds: predefinedResponse.data.juicyFeeds,
          cowsCoarseFeeds: predefinedResponse.data.cowsCoarseFeeds,
          cowsConcentratedFeeds: predefinedResponse.data.cowsConcentratedFeeds,
          breed: responsePreviousData.data?.breeds[0]?.id,
          cowsEnergyConsumptions: responsePreviousData.data?.cowsEnergyConsumptions ?? []
        }
        if(responsePreviousData.data?.youngCattleBreedId) {
          const response = await CO2Service.getHeifersPredData(
            id,
            {
              breeds: responsePreviousData.data?.breeds,
              tax: 22,
              farmerId,
              youngCattleBreedId: responsePreviousData.data?.youngCattleBreedId,
              calvesEnergyConsumptions: responsePreviousData.data?.calvesEnergyConsumptions ?? []
            }
          )
          younCalvesFeedOptions = {
            calvesCoarseFeeds: response.data.calvesCoarseFeeds,
            calvesConcentratedFeeds: response.data.calvesConcentratedFeeds
          }
        }
      }
      if (response.data.farmer.totalMilkAmount) {
        const firstStepFieldsFiltered = response.data.useMilkPrice ? firstStepFields : firstStepFields.filter(field => field !== 'milkPrice')
        const values = {}
        firstStepFieldsFiltered.map(field => {
          values[field] = response.data.farmer[field]
        })
        methods.reset({
          ...responsePreviousData.data,
          sellOtherMilkSelected: Boolean(responsePreviousData.data?.otherMilkAmount),
          ...feedOptions,
          ...younCalvesFeedOptions,
          ...values
        })
      } else {
        methods.reset({
          ...responsePreviousData.data,
          sellOtherMilkSelected: Boolean(responsePreviousData.data?.otherMilkAmount),
          ...feedOptions,
          ...younCalvesFeedOptions
        })
      }
      setOptions(response.data)
    } catch (error) {
      handleErrors(error)
    } finally {
      setIsBusy(false)
    }
  }

  async function handleDraft() {
    try {
      const response = await CO2Service.getCO2AnswerOptions(id, { farmerId })
      const responseData = await CO2Service.getCO2AnswerData(id, { farmerId })
      methods.reset({
        ...responseData.data,
        sellOtherMilkSelected: Boolean(responseData.data?.otherMilkAmount),
        grassSilage: responseData.data?.grassSilage ? {
          ...responseData.data?.grassSilage,
          step: 0
        } : null,
        maizeSilage: responseData.data?.maizeSilage ? {
          ...responseData.data?.maizeSilage,
          step: 0
        } : null
      })
      if (response.data.farmer.totalMilkAmount) {
        const firstStepFieldsFiltered = response.data.useMilkPrice ? firstStepFields : firstStepFields.filter(field => field !== 'milkPrice')
        firstStepFieldsFiltered.map(field => {
          methods.setValue(field, response.data.farmer[field])
        })
      }
      setOptions(response.data)
      setActiveTab(responseData?.data?.step || 0)
    } catch (error) {
      handleErrors(error)
    } finally {
      setIsBusy(false)
    }
  }

  async function handleEdit() {
    try {
      const response = await CO2Service.getCO2AnswerOptions(id, { farmerId })
      const responseData = await CO2Service.getCO2AnswerData(id, { farmerId })
      methods.reset({
        ...responseData.data,
        sellOtherMilkSelected: Boolean(responseData.data?.otherMilkAmount),
        grassSilage: responseData.data?.grassSilage ? {
          ...responseData.data?.grassSilage,
          step: 0
        } : null,
        maizeSilage: responseData.data?.maizeSilage ? {
          ...responseData.data?.maizeSilage,
          step: 0
        } : null
      })
      setOptions(response.data)
    } catch (error) {
      handleErrors(error)
    } finally {
      setIsBusy(false)
    }
  }

  function handleExpired() {
    notify(t('Co2 climate slot expired'), 'error')
    navigate(RouteTypes.CO2_CLIMATE)
  }

  async function fetchOptions() {
    setIsBusy(true)
    try {
      /* answerStatus = 0 - initial answer (no draft, no answer yet, co2 slot active)
      answerStatus = 1 -  edit
      answerStatus = 2 - draft
      answerStatus = 3 - not available anymore (expired) */
      const responseStatus = await CO2Service.checkCo2Status(id, { farmerId })
      switch (responseStatus.data.answerStatus) {
        //initial answer (no draft, no answer yet, co2 slot active)
        case 0:
          return handleCo2Initial()
        //edit
        case 1:
          return handleEdit()
        //draft
        case 2:
          return handleDraft()
        //not available anymore (expired)
        case 3:
          return handleExpired()
      }
    } catch (error) {
      handleErrors(error)
      navigate(RouteTypes.CO2_CLIMATE)
    }
    setIsBusy(false)
  }

  function renderSteps() {
    return <StepsContext.Provider
      value={{
        steps,
        hansleStepClick: (index) => {
          setActiveTab(index);
          methods.clearErrors();
        },
        activeTab,
        isLoadingResults,
        breed: watchBreedField?.length > 0,
        lastStepIndex: steps.length - 1
      }}
    >
      <StepsContainer />
    </StepsContext.Provider>
  }

  function renderActiveComponentStep() {
    const id = steps[activeTab].id
    switch (id) {
      case StepId.DAIRY_DATA:
        return <FirstStep
          options={options}
          setOptions={setOptions}
        />
      case StepId.DAIRY_COW_METRICS:
        return <SecondStep
          setIsLoadingResults={(value) => setIsLoadingResults(value)}
          breeds={options?.breeds || []}
        />
      case StepId.CORN_SILAGE:
        return <CornSilageStep
          options={options}
        />
      case StepId.GRASS_SILAGE:
        return <GrassSilageStep
          options={options}
          setIsLoadingResults={(value) => setIsLoadingResults(value)}
        />
      case StepId.HEIFER_METRICS:
        return <ThirdStep
          youngCattleBreeds={options?.youngCattleBreeds || []}
          heiferRaisingMethods={options?.heiferRaisingMethods || []}
          setIsLoadingResults={(value) => setIsLoadingResults(value)}
        />
      case StepId.CALF_REARING:
        return <FourthStep
          calfRearingTypes={options?.calfRearingTypes || []}
          setIsLoadingResults={(value) => setIsLoadingResults(value)}
        />
      case StepId.DAIRY_COW_HUSBANDRY:
        return <FifthStep
          housingTypes={options?.housingTypes || []}
        />
      case StepId.HEIFER_HUSBANDRY:
        return <SixthStep
          housingTypes={options?.housingTypes || []}
        />
      case StepId.DAIRY_COW_MANURE:
        return <SeventhStep
          options={options?.manureStorageTypes || []}
          manureFermentations={options?.manureFermentations || []}
        />
      case StepId.HEIFER_MANURE:
        return <EightStep
          options={options?.manureStorageTypes || []}
          manureFermentations={options?.manureFermentations || []}
        />
      case StepId.DAIRY_COW_ENERGY:
        return <NineStep
          energyConsumptions={options?.energyConsumptions}
        />
      case StepId.HEIFER_ENERGY:
        return <TenStep
          energyConsumptions={options?.energyConsumptions}
        />
      case StepId.PERFORMANCE_GROUP:
        return <ElevenStep
          milkOptions={options}
        />
      case StepId.DAIRY_COW_FEED:
        return <TwelveStep
          milkOptions={options}
        />
      case StepId.HEIFER_FEED:
        return <ThirteenStep
          milkOptions={options}
        />
      case StepId.FORAGE_FEEDS:
        return <ForageFeed
          silageCoarseFeeds={options?.silageCoarseFeeds || []}
          silageConcentratedFeeds={options?.silageConcentratedFeeds || []}
        />
      case StepId.ADDITIVES_USAGE:
        return <FourteenStep
          emissionReducingAdditives={options?.emissionReducingAdditives || []}
        />
      case StepId.LAND_USE:
        return <FifthteenStep
          landTypes={options?.landTypes || []}
        />
      case StepId.GHG_RESULT:
        return <ResultsCo2
          results={results}
        />
    }
  }

  async function handleSubmitCo2Answers(data) {
    const payload = prepareDataForPayload(data)
    setIsBusy(true)
    try {
      await CO2Service.storeCO2Answers(id, { ...payload, farmerId })
      setIsSubmitModalOpen(true)
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false)
  }

  function handleSubmitNextStep() {
    const FEED_GROUPS_TAB = steps?.findIndex(step => step.feedGroupTab)
    const ENERGIE_TAB_INDEX = steps?.findIndex(step => step.id === StepId.DAIRY_COW_ENERGY)
    const ENERGIE_TAB_HEIFER_INDEX = steps?.findIndex(step => step.id === StepId.HEIFER_ENERGY)

    /*  const DAIRY_COW_FEED_TAB = steps?.findIndex(step => step.id === StepId.DAIRY_COW_FEED)
    const HEIFER_FEED_TAB = steps?.findIndex(step => step.id === StepId.HEIFER_FEED)

    if (activeTab === DAIRY_COW_FEED_TAB || activeTab === HEIFER_FEED_TAB) {
      if (!energyCalcData || energyCalcData.energyAddition < 0 || energyCalcData.energyAddition >= 100) {
        notify(t('Please calculate energy requirements before proceeding'), 'error')
        return
      }
    } */
  
    if (activeTab === FEED_GROUPS_TAB) {
      const feedsGroup = methods.getValues('feedGroups')
      const phaseEnabled = methods.getValues('phaseFeeding')
      const feedGroups = phaseEnabled ? getTransformFeedGroups(feedsGroup) : feedsGroup
      const rearingPhases = watchRaisesHeifers ? methods.getValues('rearingPhases') : []
      let hasErrors = false
      let countCoarseFeeds = 0
      feedGroups.map(feedGroup => {
        const numberOfFeedsAssign = getNumberOfFeeds(feedGroup);
        countCoarseFeeds += feedGroup?.coarseFeeds?.length
        if (numberOfFeedsAssign === 0) {
          const feedElement = document.getElementById(feedGroup?.name);
          if (feedElement) {
            feedElement.classList.add('error-feed-group');
            hasErrors = true
          }
        }
      });
      rearingPhases.map(phase => {
        const numberOfFeedsAssign = getNumberOfFeeds(phase);
        countCoarseFeeds += phase?.coarseFeeds?.length
        if (numberOfFeedsAssign === 0) {
          const feedElement = document.getElementById(phase?.name);
          if (feedElement) {
            feedElement.classList.add('error-feed-group');
            hasErrors = true
          }
        }
      });
      if (!hasErrors && countCoarseFeeds > 0) {
        handleSaveDraft()
      }
      if (countCoarseFeeds === 0) {
        notify(t('Please select at least one coarse feed'), 'error')
      }
      return
    }
    if (activeTab === ENERGIE_TAB_INDEX) {
      const cowEnergy = methods.getValues('cowsEnergyConsumptions') || []
      const isMainEnergyError = checkPercentage(cowEnergy)
      const isRenewableEnergyError = Boolean(cowEnergy.find(field => field['slug'] === RENEWABLE_ENERGY_PROD_SLUG)) && checkPercentageChild(cowEnergy)
      if (cowEnergy.length === 0) {
        notify('Die Zusammenssetzung des Energieeinsatzes muss insgesamt 100% betragen.', 'error')
        return
      }
      if (isMainEnergyError) {
        notify('Die Zusammenssetzung des Energieeinsatzes muss insgesamt 100% betragen.', 'error')
        return
      }
      if (isRenewableEnergyError) {
        notify('Die Zusammenssetzung des Stroms aus Eigenerzeugung muss insgesamt 100% betragen.', 'error')
        return
      }
    }
    if (activeTab === ENERGIE_TAB_HEIFER_INDEX) {
      const cowEnergy = methods.getValues('calvesEnergyConsumptions') || []
      const isMainEnergyError = checkPercentage(cowEnergy)
      const isRenewableEnergyError = Boolean(cowEnergy.find(field => field['slug'] === RENEWABLE_ENERGY_PROD_SLUG)) && checkPercentageChild(cowEnergy)
      if (cowEnergy.length === 0) {
        notify('Die Zusammenssetzung des Energieeinsatzes muss insgesamt 100% betragen.', 'error')
        return
      }
      if (isMainEnergyError) {
        notify('Die Zusammenssetzung des Energieeinsatzes muss insgesamt 100% betragen.', 'error')
        return
      }
      if (isRenewableEnergyError) {
        notify('Die Zusammenssetzung des Stroms aus Eigenerzeugung muss insgesamt 100% betragen.', 'error')
        return
      }
    }
    handleSaveDraft()
  }

  async function onSubmit(data) {
    if (steps[activeTab + 1].id === StepId.GHG_RESULT) {
      setActiveTab(activeTab + 1)
      handleLoadResults(cloneDeep(data))
    } else {
      handleSubmitNextStep()
    }
  }

  function onError() {
    // TODO: Handle errors on different tabs
    //  notify('You have error on another tab', 'error');
  }

  async function handleLoadResults(data) {
    setIsLoadingResults(true)
    try {
      const payload = prepareDataForPayload(data)
      payload.growSilage = watchGrowSilage?.find(item => item === 1 || item === 2) ? 1 : 0
      await CO2Service.saveCo2Draft(id, { ...payload, farmerId })
      const response = await CO2Service.getResults(id, { ...payload, farmerId })
      setResults(response.data)
    } catch (error) {
      const cowFeedStep = steps.findIndex(step => step.id === StepId.DAIRY_COW_FEED)
      if(cowFeedStep >= 0) {
        setActiveTab(cowFeedStep)
      } else {
        setActiveTab(activeTab - 1)
      }
      handleErrors(error)
    }
    setIsLoadingResults(false)
  }

  function handleSubmitPhases() {
    const tempFeedGroups = cloneDeep(watchFeedGroups)
    const selectedFeedGroups = methods.getValues('selectedFeedGroups')
    const feedingPhases = methods.getValues('feedingPhases')

    tempFeedGroups.forEach((feedGroup, index) => {
      const selectedGroup = selectedFeedGroups.find(selectedFeedGroupIndex => selectedFeedGroupIndex === index)
      if (selectedGroup !== undefined) {
        if (feedGroup.feedingPhases?.length > 0) {
          feedGroup.feedingPhases.forEach((feedingPhase, index) => {
            const phaseData = index === 0 ? feedingPhases[0] : feedingPhases[1]
            feedingPhase = {
              ...feedingPhase,
              ...phaseData
            }
          })
        } else {
          feedGroup.feedingPhases = [
            {
              ...feedingPhases[0],
              concentratedFeeds: [],
              juicyFeeds: [],
              coarseFeeds: []
            },
            {
              ...feedingPhases[1],
              concentratedFeeds: [],
              juicyFeeds: [],
              coarseFeeds: []
            }
          ]
        }
      } else {
        tempFeedGroups[index] = {
          ...feedGroup,
          feedingPhases: []
        }
      }
    })
    methods.setValue('feedGroups', tempFeedGroups)
    setFeedPage(1)
  }

  function handlePreviousFutterbau() {
    const grassSilageStep = methods.getValues('grassSilage.step') || 0
    const cornSilageStep = methods.getValues('maizeSilage.step') || 0
    const currentFutterBauStep = steps[activeTab]?.id === StepId.GRASS_SILAGE ? grassSilageStep : cornSilageStep

    if (currentFutterBauStep === 0) {
      setActiveTab(activeTab - 1)
      return
    }

    if (steps[activeTab]?.id === StepId.GRASS_SILAGE) {
      setValue('grassSilage.step', grassSilageStep - 1)
      return
    }
    setValue('maizeSilage.step', cornSilageStep - 1)
  }

  function renderPreviousButton() {
    
    return <Button
      type='button'
      disabled={isLoadingResults || isEnergyDataFetching || loadingEnergyData}
      loading={isLoadingResults || isEnergyDataFetching || loadingEnergyData}
      onClick={() => {
        methods.clearErrors()
        const isFutterBauStep = steps[activeTab]?.futterbauStep
        if (isFutterBauStep) {
          handlePreviousFutterbau()
          return
        }
        setActiveTab(activeTab - 1)
      }}
      content={t('Previous')}
    />
  }

  function renderNextButton() {
    return <Button
      type={'submit'}
      loading={isLoadingResults}
      style={{ minWidth: 'fit-content' }}
      disabled={!enableNextButton}
      content={'Speichern & Weiter'}
    />
  }

  function renderSubmitButton() {
    return <Button
      style={{ minWidth: 225, color: 'white' }}
      type={'button'}
      disabled={Object.keys(methods.formState.errors).length > 0 || isLoadingResults}
      content={t('Complete data collection')}
      onClick={() => handleSubmitCo2Answers(cloneDeep(methods.getValues()))}
    />
  }

  function renderAssignFeedButton() {
    return <Button
      type={'button'}
      style={{ minWidth: 200 }}
      content={t('Assign feeds')}
      onClick={() => {
        const selectedFeedGroups = methods.getValues('selectedFeedGroups')
        const feedingPhases = methods.getValues('feedingPhases')
        const silageTypes = methods.getValues('silageTypes') || []

        if (silageTypes.length === 0) {
          methods.setError('silageTypes', { message: '' })
          return
        }

        if (feedingPhases?.length > 0 && (!selectedFeedGroups || selectedFeedGroups?.length === 0)) {
          notify('Bitte wählen Sie eine Leistungsgruppe aus.', 'error')
          return
        }
        if (watchRaisesHeifers && !watchRearingPhaseType) {
          methods.setError('rearingPhasesType', { message: '' })
          return
        }
        if (watchPhaseFeeding) {
          handleSubmitPhases()
        } else {
          setFeedPage(1)
        }
      }}
    />
  }

  function renderBackToFeedGroups() {
    return <Button
      type={'button'}
      style={{ minWidth: 210 }}
      content={'Zurück'}
      onClick={() => setFeedPage(0)}
    />
  }

  async function getEnergyData() {
    const data = methods.getValues()
    const payload = prepareDataForPayload(data)
    const cowsFeedRationsTab = steps[activeTab].id === StepId.DAIRY_COW_FEED
    const feedRationsType = cowsFeedRationsTab ? 'cows' : 'heifers' as ClimaCheckFeedType
    setLoadingEnergyData(true)
    try {
      const response = await CO2Service.calculateEnergyData(id, feedRationsType, { ...payload, farmerId })
      const isEnergyCorrect = response.data?.energyAddition > 0 && response.data?.energyAddition < 30
      setIsEnergyModalOpen(!isEnergyCorrect)
      setEnergyCalcData(response.data)
    } catch (error) {
      handleErrors(error)
    } finally {
      setLoadingEnergyData(false)
    }
  }

  function validateDryMatterTotals() {
    const values = methods.getValues()
    const feedGroupsData = getTransformCowsFeedGroups(values.feedGroups)
    const warnings: { groupName: string; value: number; min: number; max: number }[] = []
    const errors: { groupName: string; value: number; min: number; max: number }[] = []
    feedGroupsData.forEach(feedGroup => {
      if (!feedGroup.validation) return
  
      const dryMatterSum = getCowTotalDryMatter(feedGroup, {
        cowsCoarseFeeds: values?.cowsCoarseFeeds || [],
        cowsConcentratedFeeds: values?.cowsConcentratedFeeds || [],
        juicyFeeds: values?.juicyFeeds || []
      })
      const { limitMinValue, limitMaxValue, warningMinValue, warningMaxValue } = feedGroup.validation
      const roundedSum = Math.round(dryMatterSum * 100) / 100
  
      if (roundedSum < limitMinValue || roundedSum > limitMaxValue) {
        errors.push({
          groupName: feedGroup.name,
          value: roundedSum,
          min: limitMinValue,
          max: limitMaxValue
        })
      } else if (roundedSum < warningMinValue || roundedSum > warningMaxValue) {
        warnings.push({
          groupName: feedGroup.name,
          value: roundedSum,
          min: warningMinValue,
          max: warningMaxValue
        })
      }
    })
  
    return { warnings, errors }
  }

  function renderCalcEnergyButton() {
    const isHefeirsStep = steps[activeTab].id === StepId.HEIFER_FEED
    const energyButtonTitle = isHefeirsStep ? 'Futterwerte übernehmen' : t('Calculate energy requirements')
    return <Button
      type='button'
      content={energyButtonTitle}
      onClick={() => {
        if (isHefeirsStep) {
          // Validate young cattle feed totals for heifer step
          const values = methods.getValues()
          const { warnings: youngCattleWarnings, errors: youngCattleErrors } = validateYoungCattleFeedTotals(
            values.rearingPhasesType,
            values.rearingPhases || [],
            options?.youngCattleValidationFeedGroups || [],
            {
              calvesCoarseFeeds: values?.calvesCoarseFeeds || [],
              calvesConcentratedFeeds: values?.calvesConcentratedFeeds || []
            }
          )
        
          if (youngCattleErrors.length > 0 || youngCattleWarnings.length > 0) {
            setYoungCattleFeedValidationModal({ isOpen: true, warnings: youngCattleWarnings, errors: youngCattleErrors })
            return
          }
                  
        } else {
          // Validate dry matter totals for cows step
          const { warnings, errors } = validateDryMatterTotals()
        
          if (errors.length > 0) {
            setDryMatterValidationModal({ isOpen: true, warnings, errors })
            return
          }
        
          if (warnings.length > 0) {
            setDryMatterValidationModal({ isOpen: true, warnings, errors })
            return
          }
        }
        
        getEnergyData()
      }}
      loading={loadingEnergyData || isLoadingResults || isEnergyDataFetching}
      disabled={loadingEnergyData || isLoadingResults || isEnergyDataFetching}
      style={{ minWidth: 250 }}
    />
  }

  function renderAnwseredPercent() {
    return <div style={{ display: 'flex', alignItems: 'anchor-center', gap: 20 }}>
      <AnsweredContainer>
        <Progress value={activeTab + 1} total={steps.length} progress={'ratio'} />
      </AnsweredContainer>
      {activeTab === LAST_STEP && renderSubmitButton()}
    </div>
  }

  async function handleSaveDraft() {

    if (watchBreedField?.length > 0 && (!watchFeedGroups || watchFeedGroups?.length === 0)) {
      return notify('Es fehlt Laktierende oder Trockensteher. Bitte Rasse erneut auswählen.', 'error')
    }

    const grassSilageStep = methods.getValues('grassSilage.step') || 0
    const cornSilageStep = methods.getValues('maizeSilage.step') || 0

    const data = methods.getValues()
    const payload = prepareDataForPayload(data)
    setIsLoadingResults(true)
    const maxStep = activeTab + 1 > lastStep ? activeTab + 1 : lastStep
    const isFutterBauStep = steps[activeTab]?.futterbauStep
    const currentFutterBauStep = steps[activeTab]?.futterbauStep ?
      steps[activeTab]?.id === StepId.GRASS_SILAGE ? grassSilageStep : cornSilageStep : 3

    // update if we are not in grassilage or cornsilage
    // update if we are in grassilage or cornsilage but on last step
    const disableUpdateActiveTab = isFutterBauStep && currentFutterBauStep < MAX_FUTTERBAU_STEP

    try {
      const grassSilageActiveTab = steps[activeTab].id == StepId.GRASS_SILAGE
      const cornSilageActiveTab = steps[activeTab].id == StepId.CORN_SILAGE
      await CO2Service.saveCo2Draft(id, {
        ...payload,
        step: disableUpdateActiveTab ? data.step : maxStep,
        farmerId,
        growSilage: watchGrowSilage?.find(item => item === 1 || item === 2) ? 1 : 0,
        grassSilage: {
          ...payload.grassSilage,
          step: grassSilageActiveTab && disableUpdateActiveTab ? grassSilageStep + 1 : grassSilageStep
        },
        maizeSilage: {
          ...payload.maizeSilage,
          step: cornSilageActiveTab && disableUpdateActiveTab ? cornSilageStep + 1 : cornSilageStep
        }
      })

      if (!disableUpdateActiveTab) {
        if (isFutterBauStep) {
          setValue('grassSilage.step', 0)
          setValue('maizeSilage.step', 0)
        }
        setActiveTab(activeTab + 1)
        return
      }
      if (grassSilageActiveTab) {
        setValue('grassSilage.step', grassSilageStep + 1)
        return
      }

      setValue('maizeSilage.step', cornSilageStep + 1)
    } catch (error) {
      handleErrors(error)
    } finally {
      setIsLoadingResults(false)
    }
  }

  function renderEnergyModal() {
    const isHefeirsStep = steps[activeTab].id === StepId.HEIFER_FEED
    return <EnergyModal
      energyCalcData={energyCalcData}
      isEnergyModalOpen={isEnergyModalOpen}
      handleClose={() => setIsEnergyModalOpen(false)}
      heiferEnergyModal={isHefeirsStep}
    />
  }

  function renderSubmitModal() {
    return <Dialog
      textHeader={''}
      isDialogOpen={isSubmitModalOpen}
      textSubmit={t('Back to home page')}
      hideCloseIcon
      handleSubmit={() => {
        setIsSubmitModalOpen(false)
        navigate(RouteTypes.CO2_CLIMATE)
      }
      }
      textContent={<div style={{ textAlign: 'center' }}>
        <div style={{ marginBottom: 20 }}>
          <SubmitHeader>{'Vielen Dank für Ihre Dateneingabe.'}</SubmitHeader>
        </div>
        <div style={{ marginBottom: 20 }}>
          <div>{'Sie haben die Möglichkeit die Daten im Rahmen des Erhebungszeitraums zu ändern.'}</div>
          <div>{'Nach dem Ende des Erhebungszeitraums erhalten Sie ein ausführliches PDF-Dokument mit allen Auswertungen und Eingabewerten.'}</div>
        </div>
        <LogoWrapper style={{ backgroundImage: `url(${logoTitle})` }} />
      </div>}
    />
  }

  function renderDryMatterModal() {
    return <DryMatterValuesModal
      isOpen={dryMatterValidationModal.isOpen}
      warnings={dryMatterValidationModal.warnings}
      errors={dryMatterValidationModal.errors}
      handleClose={() => setDryMatterValidationModal({ isOpen: false, warnings: [], errors: [] })}
      handleContinue={() => {
        setDryMatterValidationModal({ isOpen: false, warnings: [], errors: [] })
        getEnergyData()
      }}
    />
  }

  function renderYoungCattleFeedValidationModal() {
    return <YoungCattleFeedValidationModal
      isOpen={youngCattleFeedValidationModal.isOpen}
      warnings={youngCattleFeedValidationModal.warnings}
      errors={youngCattleFeedValidationModal.errors}
      handleClose={() => setYoungCattleFeedValidationModal({ isOpen: false, warnings: [], errors: [] })}
      handleContinue={() => {
        setYoungCattleFeedValidationModal({ isOpen: false, warnings: [], errors: [] })
        getEnergyData()
      }}
    />
  }

  const LAST_STEP = steps.length - 1

  // we cannon procced if we have not selected breed unless we are on first step and on the last step we have to submit
  // we enable in case grass_silage and corn_silage steps
  const enableNextButton =
    activeTab === 0 ||
    (activeTab < LAST_STEP && Boolean(watchBreedField) && watchBreedField?.length > 0) ||
    steps[activeTab]?.futterbauStep

  const FEED_GROUPS_TAB = steps?.findIndex(step => step.feedGroupTab)

  const assignFeedButton = (activeTab === FEED_GROUPS_TAB) && (feedPage === 0)

  const backToFeedGroups = (activeTab === FEED_GROUPS_TAB) && (feedPage === 1)

  const isFeedRationsTabsActive = getFeedRationsTabActive()

  const displayEnergyCalcData = isFeedRationsTabsActive && (!energyCalcData || (energyCalcData?.energyAddition < 0 || energyCalcData?.energyAddition >= 100))

  return (
    <Layout
      title={t('Climate check')}
      segmented
      close={RouteTypes.CO2_CLIMATE}
      isBusy={isBusy}
      isCo2Loader={true}
      actions={renderAnwseredPercent()}
    >
      {renderEnergyModal()}
      {renderSubmitModal()}
      {renderDryMatterModal()}
      {renderYoungCattleFeedValidationModal()}
      <CheckClimaEl.StepsContainer>
        {renderSteps()}
        {!steps[activeTab]?.futterbauStep && <Message info style={{
          borderRadius: '10px',
          marginLeft: 'auto',
          marginRight: 'auto',
          width: 'fit-content',
          textAlign: 'center'
        }}>
          <Message.Content>
            {!isBusy && <div dangerouslySetInnerHTML={{ __html: getStepHeadMessage(steps[activeTab].id as StepId) }} />}
          </Message.Content>
        </Message>}
      </CheckClimaEl.StepsContainer>
      <CheckClimaEl.WizzardContentWrapper onSubmit={methods.handleSubmit(onSubmit)}>
        <CheckClimaEl.StyledContent $isFirstStep={activeTab === 0}>
          <FeedPageContext.Provider value={{
            feedPage,
            setFeedPage,
            energyCalcData,
            setEnergyCalcData,
            loadingEnergyData,
            isEnergyDataFetching,
            setIsEnergyDataFetching,
            handleSubmit: methods.handleSubmit(onSubmit)
          }}>
            <FormProvider {...methods}>
              {!isBusy && options && renderActiveComponentStep()}
            </FormProvider>
          </FeedPageContext.Provider>
        </CheckClimaEl.StyledContent>
        <CheckClimaEl.ActionsContainer $activeTab={activeTab}>
          {activeTab > FIRST_STEP_INDEX && !backToFeedGroups && renderPreviousButton()}
          {backToFeedGroups && renderBackToFeedGroups()}
          {enableNextButton && !assignFeedButton && !displayEnergyCalcData && renderNextButton()}
          {assignFeedButton && renderAssignFeedButton()}
          {activeTab === LAST_STEP && renderSubmitButton()}
          {displayEnergyCalcData && renderCalcEnergyButton()}
        </CheckClimaEl.ActionsContainer>
      </CheckClimaEl.WizzardContentWrapper>
    </Layout >
  );
};

export default AnswerCO2Climate;